import {
  createContext,
  useContext,
  useCallback,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { ChargebeeCustomer } from '../utils/types';

export interface CustomerState {
  customer: ChargebeeCustomer | null;
  isLoading: boolean;
  isError: boolean;
  hasCustomer: boolean;
}

interface AppContextType {
  pCode: string;
  formattedPCode: string;
  user?: any;
  // Customer state
  customerState: CustomerState;
  setCustomerState: (state: CustomerState) => void;
  // Subscribe flow
  isCreateCustomerModalOpen: boolean;
  openCreateCustomerModal: () => void;
  closeCreateCustomerModal: () => void;
  // Trigger subscribe - will open modal if no customer, otherwise call the callback
  triggerSubscribe: (onSubscribe: () => void) => void;
  pendingSubscribeCallback: (() => void) | null;
  setPendingSubscribeCallback: Dispatch<
    SetStateAction<(() => void) | null>
  >;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
  pCode: string;
  user?: any;
  customerState: CustomerState;
  setCustomerState: (state: CustomerState) => void;
  isCreateCustomerModalOpen: boolean;
  openCreateCustomerModal: () => void;
  closeCreateCustomerModal: () => void;
  pendingSubscribeCallback: (() => void) | null;
  setPendingSubscribeCallback: Dispatch<
    SetStateAction<(() => void) | null>
  >;
}

export const AppProvider = ({
  children,
  pCode,
  user,
  customerState,
  setCustomerState,
  isCreateCustomerModalOpen,
  openCreateCustomerModal,
  closeCreateCustomerModal,
  pendingSubscribeCallback,
  setPendingSubscribeCallback,
}: AppProviderProps) => {
  const triggerSubscribe = useCallback(
    (onSubscribe: () => void) => {
      const hasConfirmedCustomer =
        !customerState.isLoading &&
        !customerState.isError &&
        customerState.hasCustomer &&
        customerState.customer != null;

      if (hasConfirmedCustomer) {
        // Customer exists, proceed with subscribe
        onSubscribe();
      } else {
        // No customer, store callback and open modal
        setPendingSubscribeCallback(() => onSubscribe);
        openCreateCustomerModal();
      }
    },
    [
      customerState.customer,
      customerState.hasCustomer,
      customerState.isError,
      customerState.isLoading,
      openCreateCustomerModal,
      setPendingSubscribeCallback,
    ],
  );

  const contextValue: AppContextType = {
    pCode,
    formattedPCode: `p-${pCode}`,
    user,
    customerState,
    setCustomerState,
    isCreateCustomerModalOpen,
    openCreateCustomerModal,
    closeCreateCustomerModal,
    triggerSubscribe,
    pendingSubscribeCallback,
    setPendingSubscribeCallback,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};
